import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import {
  audienceGroupsRefusalOf,
  type AudienceGroupsRefusal,
  type ProgramAudienceGroups
} from '../../../../../../api/audienceRequirements';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../../../redux/hooks';
import type {
  AudienceRule,
  EnrollmentMode,
  LearningPillar,
  PillarIcon,
  ProgramCardData,
  ProgramFormat,
  ProgramOwner,
  ProgramTypeKey
} from './types';
import { flattenAudienceRules, participatingAudienceRules } from '../../../../utils/memberAttributes';
import { normalizeProgramDueDate } from './programDueDate';
import { readSessionProfile } from '../../../../../../auth/session';

// Live data for the Admin Learning Framework Dashboard.
//
// Reads:
//   GET /orgs/{orgId}/pillars
//   GET /v3/admin/programs
//   GET /getUsersByOrg/{orgId}             (org members for the owner picker)
// Mutations:
//   POST  /orgs/{orgId}/pillars           (createPillar)
//   PATCH /pillars/{id}                   (updatePillar)
//   DELETE /pillars/{id}                  (deletePillar; cascades programs to null)
//   PATCH /programs/{id}                  (moveProgram / setAudience / setOwner)
//   PATCH /group/{grpId}/launchDate       (updateProgramLaunchDate)
//   POST  /group/create                   (drawer Design Now / Design Later)

const getAuthHeaders = () => {
  try {
    const raw = localStorage.getItem('tokens');
    const tokens = raw ? JSON.parse(raw) : {};
    // orgid — the Auth0 org identifier read from the access token claims.
    // Forwarded as a request header so the BE's verifyOrganization middleware
    // can resolve the org without needing it in the request body.
    const accessRaw = tokens.access_token || '';
    let orgid = '';
    try {
      const payload = JSON.parse(atob(accessRaw.split('.')[1] || ''));
      orgid = payload?.['https://dosen.io/organization']?.id || '';
    } catch {
      // malformed token — orgid stays empty, BE falls back to body orgId
    }
    // Transparent authentication: the tokens carry no organization claim — the session does.
    if (!orgid) orgid = readSessionProfile()?.organization?.id || '';
    return { idtoken: tokens.id_token || '', authorization: tokens.access_token || '', orgid };
  } catch {
    return { idtoken: '', authorization: '', orgid: '' };
  }
};

const toPillar = (p: any): LearningPillar => ({
  id: String(p.id),
  name: p.name ?? '',
  order: typeof p.order === 'number' ? p.order : 0,
  icon: (p.icon ?? null) as PillarIcon | null
});

// BE returns an empty shape `{ id: null, name: " ", headshot: "" }` when
// the field is unset (instead of just `null`). Treat anything without a
// real id as null so the ProgramCard's `owner ?? creator` fallback works.
const toOwner = (raw: any): ProgramOwner | null => {
  if (!raw) return null;
  const id = raw.id != null ? String(raw.id) : '';
  if (!id) return null;
  return { id, name: (raw.name ?? '').trim() || '', headshot: raw.headshot || '' };
};

const toProgram = (p: any): ProgramCardData => ({
  id: String(p.id),
  learningPillarId: p.learningPillarId ?? '',
  name: p.name ?? '',
  // BE returns null for legacy programs without a category. Default to
  // 'role-excellence' so the colour dot doesn't crash; programs without a
  // pillar are filtered out at the render layer anyway.
  programType: (p.programType ?? 'role-excellence') as ProgramTypeKey,
  model: p.model ?? null,
  status: p.status ?? 'design',
  audience: Array.isArray(p.audience) && p.audience.length > 0 ? p.audience : null,
  audienceRules:
    Array.isArray(p.audienceRules) && p.audienceRules.length > 0 ? (p.audienceRules as AudienceRule[]) : null,
  launchDate: typeof p.launchDate === 'number' ? p.launchDate : null,
  programDueDate: normalizeProgramDueDate(p.programDueDate),
  programCompletionRate: typeof p.programCompletionRate === 'number' ? p.programCompletionRate : 0,
  owner: toOwner(p.owner),
  creator: toOwner(p.creator),
  displayOrder: typeof p.displayOrder === 'number' ? p.displayOrder : 0,
  locked: p.locked === true,
  // A BE that predates `canEdit` only served admins with edit rights.
  canEdit: p.canEdit !== false,
  // The build flow (absent from a BE that predates them, and empty for programs built another way).
  roleIds: Array.isArray(p.roleIds) ? p.roleIds.map(String) : [],
  capabilityIds: Array.isArray(p.capabilityIds) ? p.capabilityIds.map(String) : [],
  format: p.format === 'course' || p.format === 'shortCourse' || p.format === 'brief' ? p.format : null,
  guided: p.guided === true
});

export interface DrawerCreateProgramInput {
  name: string;
  programType: ProgramTypeKey;
  learningPillarId: string | null;
  audienceRules: AudienceRule[];
  // Enrollment intent from the Elliott chat — the BE executes it at create.
  enrollmentMode?: EnrollmentMode | null;
  // Creation-flow revamp: store the audience only; enrol on Invite & Finish.
  awaitingInviteFinish?: boolean;
  launchDate: number | null;
  ownerId: string | null;
  // The build flow: what the program is built from (the BE keeps them with it).
  roleIds?: string[];
  capabilityIds?: string[];
  format?: ProgramFormat | null;
  guided?: boolean;
  // Shared capabilities' build rules (every goal a lesson of its own, PRD R10): the build flow sends it.
  goalLessons?: boolean;
  // Audience Requirements (DA-5056), with its backend on: the Read & Assess and Read Only groups are the
  // audience. Sent, the roles, the narrowing and the enrolment mode are left out: the BE derives them.
  audienceGroups?: ProgramAudienceGroups;
}

export interface UseAdminLearningLifecycleResult {
  pillars: LearningPillar[];
  programs: ProgramCardData[];
  orgMembers: ProgramOwner[];
  loading: boolean;
  error: string | null;
  refetch: () => void;

  createPillar: (input: { name: string; icon: PillarIcon; order?: number }) => Promise<LearningPillar | null>;
  updatePillar: (id: string, changes: Partial<Pick<LearningPillar, 'name' | 'icon' | 'order'>>) => Promise<LearningPillar | null>;
  deletePillar: (id: string) => Promise<boolean>;
  moveProgram: (programId: string, learningPillarId: string | null) => Promise<boolean>;
  // Persist a batch of card position changes (drag-to-reorder within / across
  // pillar columns). Each entry sets the card's pillar + its new displayOrder.
  reorderPrograms: (
    updates: Array<{ id: string; learningPillarId: string | null; displayOrder: number }>
  ) => Promise<boolean>;
  updateProgramLaunchDate: (programId: string, launchDate: number | null) => Promise<boolean>;
  updateProgramAudience: (programId: string, rules: AudienceRule[] | null) => Promise<boolean>;
  /** Read-only impact of a pending audience change. Returns null when the
   *  preview call fails, which callers treat as "save without confirmation". */
  previewProgramAudience: (
    programId: string,
    rules: AudienceRule[] | null
  ) => Promise<{ toAdd: number; toRemove: number } | null>;
  updateProgramOwner: (programId: string, ownerId: string | null) => Promise<boolean>;
  updateProgramType: (programId: string, programType: ProgramTypeKey) => Promise<boolean>;
  /**
   * Resolves the new program's id, or null when it could not be created: the reason has been shown,
   * except a refusal of the audience groups naming who would be in both (`refusal.overlaps`), which is
   * the caller's to show beside the groups.
   */
  createProgramFromDrawer: (
    input: DrawerCreateProgramInput
  ) => Promise<{ id: string | null; refusal?: AudienceGroupsRefusal }>;
  // Mentorship publish — PATCH /programs/{id} { goalPublished, settingsUpdate },
  // the same flags the legacy final step set. Without it the program stays in draft.
  publishMentorshipProgram: (programId: string) => Promise<boolean>;
  // Org-scoped admin invite (Elliott menu's Invite Admin flow).
  inviteAdmin: (data: { firstName: string; lastName: string; email: string }) => Promise<boolean>;
  promoteOrgAdmin: (userId: string) => Promise<boolean>;
}

// Normalises the (BE-enriched) org-members payload to the FE owner shape.
// BE now returns `id` = Mongo _id and `name` = "First Last" sourced from
// our DB's personal_details. Prefer those; the Auth0 fields (user_id,
// given_name, family_name) remain on the payload but are now fallbacks.
// One row of GET /getUsersByOrg/{orgId} as a person to pick (owner, manager).
export const toOrgMember = (u: any): ProgramOwner => {
  const id = String(u.id || u.user_id || '');
  const givenFamily = [u.given_name, u.family_name].filter(Boolean).join(' ');
  const rawName = String(u.name || '');
  const nameIsEmail = rawName.includes('@');
  return {
    id,
    name: (!nameIsEmail && rawName) || givenFamily || rawName,
    headshot: u.headshot || u.picture
  };
};

export const useAdminLearningLifecycle = (): UseAdminLearningLifecycleResult => {
  const { orgId, location, isPlatformAdmin } = useProgramContext();
  // For POST /group/create we need the Mongo identifiers — `orgId` from
  // useProgramContext is the Auth0 authId, which the legacy createGroup
  // path doesn't accept (it passes `parent` straight into getGroup keyed
  // on Mongo _id). Pull both from the orgDetails slice, same as the
  // legacy ManualProgramBuilder.
  const orgMongoId = useAppSelector((state) => state.orgDetails.data?._id) || '';
  const orgParentGroupId = useAppSelector((state) => state.orgDetails.data?.grpId) || '';
  const [pillars, setPillars] = useState<LearningPillar[]>([]);
  const [programs, setPrograms] = useState<ProgramCardData[]>([]);
  const [orgMembers, setOrgMembers] = useState<ProgramOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    const { idtoken, authorization } = getAuthHeaders();
    if (!idtoken || !authorization) {
      setError('Missing auth tokens');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // allSettled (not all): the three calls are independent, so a failure of
      // pillars or members must NOT blank the programs board. Previously a
      // single rejected sibling sent the whole thing to the catch and left
      // `programs` empty even when its own call returned 200 — the symptom
      // platform admins saw as "no programs on the dashboard".
      const [pillarsR, programsR, membersR] = await Promise.allSettled([
        API.getLearningPillars({ orgId, idtoken, authorization, location }),
        // Platform admin must pass orgId as a query param; for org/group
        // admins the BE infers org from JWT.
        API.getV3AdminPrograms({
          orgId: isPlatformAdmin ? orgId : undefined,
          idtoken,
          authorization,
          location
        }),
        API.getUsersByOrgId(authorization, orgId)
      ]);
      const pillarsRes = pillarsR.status === 'fulfilled' ? pillarsR.value : null;
      const programsRes = programsR.status === 'fulfilled' ? programsR.value : null;
      const membersRes = membersR.status === 'fulfilled' ? membersR.value : null;
      // Only the programs call failing is fatal to the board; degrade the
      // others (empty pillars / no owner names) but still render the cards.
      if (programsR.status === 'rejected') {
        const e: any = programsR.reason;
        setError(e?.response?.data?.message || e?.message || 'Failed to load programs');
      }
      const pillarList: any[] = Array.isArray(pillarsRes?.data?.pillars) ? pillarsRes!.data.pillars : [];
      const programList: any[] = Array.isArray(programsRes?.data?.programs) ? programsRes!.data.programs : [];
      const memberList: any[] = Array.isArray(membersRes?.data) ? membersRes!.data : [];
      const mappedPillars: LearningPillar[] = pillarList.map(toPillar);
      // Sort: real names first (no '@'), alphabetical; then the
      // email-fallback rows (members whose DB doc lacks first/last
      // names — `toOrgMember` left their email as the display value),
      // also alphabetical. Case-insensitive compare so casing doesn't
      // scramble the order.
      const mappedMembers = memberList
        .map(toOrgMember)
        .filter((m) => m.id)
        .sort((a, b) => {
          const aIsEmail = a.name.includes('@');
          const bIsEmail = b.name.includes('@');
          if (aIsEmail !== bIsEmail) return aIsEmail ? 1 : -1;
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
      const memberMap = new Map(mappedMembers.map((m) => [m.id, m]));
      // Cross-reference owner/creator with the member list so display names
      // are always correct, even when the program endpoint returns emails.
      const resolveOwner = (o: ProgramOwner | null) =>
        o ? (memberMap.get(o.id) ?? o) : null;
      setPillars(mappedPillars.sort((a, b) => a.order - b.order));
      const mappedPrograms = programList.map(toProgram).map((p) => ({
        ...p,
        owner: resolveOwner(p.owner),
        creator: resolveOwner(p.creator)
      }));
      setPrograms(mappedPrograms);
      setOrgMembers(mappedMembers);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Programs could not be loaded. Refresh the page and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, location, isPlatformAdmin]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const createPillar: UseAdminLearningLifecycleResult['createPillar'] = useCallback(
    async (input) => {
      if (!orgId) return null;
      const { idtoken, authorization } = getAuthHeaders();
      try {
        const res = await API.createLearningPillar({
          orgId,
          body: input,
          idtoken,
          authorization,
          location
        });
        const created = res?.data?.pillar ? toPillar(res.data.pillar) : null;
        if (created) setPillars((prev) => [...prev, created].sort((a, b) => a.order - b.order));
        return created;
      } catch (e) {
        return null;
      }
    },
    [orgId, location]
  );

  const updatePillar: UseAdminLearningLifecycleResult['updatePillar'] = useCallback(
    async (id, changes) => {
      const { idtoken, authorization } = getAuthHeaders();
      try {
        const res = await API.updateLearningPillar({
          id,
          body: changes,
          idtoken,
          authorization,
          location
        });
        const updated = res?.data?.pillar ? toPillar(res.data.pillar) : null;
        if (updated) {
          setPillars((prev) =>
            prev.map((p) => (p.id === id ? updated : p)).sort((a, b) => a.order - b.order)
          );
        }
        return updated;
      } catch (e) {
        return null;
      }
    },
    [location]
  );

  const deletePillar: UseAdminLearningLifecycleResult['deletePillar'] = useCallback(
    async (id) => {
      const { idtoken, authorization } = getAuthHeaders();
      try {
        await API.deleteLearningPillar({ id, idtoken, authorization, location });
        setPillars((prev) => prev.filter((p) => p.id !== id));
        // Cascade-to-null happened on the BE; mirror it locally so any
        // affected program cards drop out of the pillar grid immediately.
        setPrograms((prev) =>
          prev.map((p) => (p.learningPillarId === id ? { ...p, learningPillarId: '' } : p))
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    [location]
  );

  const moveProgram: UseAdminLearningLifecycleResult['moveProgram'] = useCallback(
    async (programId, learningPillarId) => {
      const { idtoken, authorization, orgid } = getAuthHeaders();

      // Capture the original pillar before the optimistic update so we can
      // roll back just this one card on failure (instead of calling fetchAll
      // which resets all state and causes every card to snap back).
      const originalPillarId = programs.find((p) => p.id === programId)?.learningPillarId ?? '';

      // Optimistic update — card visually lands in destination column immediately.
      setPrograms((prev) =>
        prev.map((p) => (p.id === programId ? { ...p, learningPillarId: learningPillarId ?? '' } : p))
      );
      try {
        await API.patchProgram({
          id: programId,
          body: { learningPillarId },
          idtoken,
          authorization,
          location,
          orgid
        });
        return true;
      } catch (e) {
        // Targeted rollback — only restore this card; leave the rest of the
        // board state intact. Avoids the full-refetch snap-back of all cards.
        console.error('[moveProgram] PATCH failed, rolling back card:', e);
        setPrograms((prev) =>
          prev.map((p) => (p.id === programId ? { ...p, learningPillarId: originalPillarId } : p))
        );
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, programs]
  );

  const reorderPrograms: UseAdminLearningLifecycleResult['reorderPrograms'] = useCallback(
    async (updates) => {
      if (updates.length === 0) return true;
      const { idtoken, authorization, orgid } = getAuthHeaders();

      // Snapshot for an all-or-nothing rollback — reorders touch several cards,
      // so a partial failure would leave the column visually inconsistent.
      const snapshot = programs;
      const byId = new Map(updates.map((u) => [u.id, u]));

      // Optimistic: apply every position change at once so the column settles
      // into its new order immediately.
      setPrograms((prev) =>
        prev.map((p) => {
          const u = byId.get(p.id);
          return u
            ? { ...p, learningPillarId: u.learningPillarId ?? '', displayOrder: u.displayOrder }
            : p;
        })
      );

      try {
        await Promise.all(
          updates.map((u) => {
            const cur = snapshot.find((p) => p.id === u.id);
            const body: { displayOrder: number; learningPillarId?: string | null } = {
              displayOrder: u.displayOrder
            };
            // Only re-send the pillar when it actually changed (cross-column
            // move) — avoids the BE's pillar-existence lookup on pure reorders.
            if ((cur?.learningPillarId || '') !== (u.learningPillarId || '')) {
              body.learningPillarId = u.learningPillarId;
            }
            return API.patchProgram({ id: u.id, body, idtoken, authorization, location, orgid });
          })
        );
        return true;
      } catch (e) {
        console.error('[reorderPrograms] PATCH failed, rolling back column:', e);
        setPrograms(snapshot);
        toast.error("Couldn't save the new order");
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, programs]
  );

  const updateProgramLaunchDate: UseAdminLearningLifecycleResult['updateProgramLaunchDate'] = useCallback(
    async (programId, launchDate) => {
      const { idtoken, authorization } = getAuthHeaders();
      setPrograms((prev) => prev.map((p) => (p.id === programId ? { ...p, launchDate } : p)));
      try {
        await API.updateGroupLaunchDate({ grpId: programId, launchDate, idtoken, authorization, location });
        return true;
      } catch (e) {
        await fetchAll();
        return false;
      }
    },
    [location, fetchAll]
  );

  const previewProgramAudience: UseAdminLearningLifecycleResult['previewProgramAudience'] = useCallback(
    async (programId, rules) => {
      const { idtoken, authorization } = getAuthHeaders();
      try {
        const res = await API.previewProgramAudience({
          id: programId,
          audienceRules: participatingAudienceRules(rules),
          idtoken,
          authorization,
          location
        });
        const data = res?.data?.data ?? res?.data ?? {};
        return {
          toAdd: Number(data.toAdd) || 0,
          toRemove: Number(data.toRemove) || 0
        };
      } catch (e) {
        // Preview is advisory. If it fails we return null and the caller saves
        // without the impact confirmation rather than blocking the edit.
        console.error('[LL] audience preview failed', e);
        return null;
      }
    },
    [location]
  );

  const updateProgramAudience: UseAdminLearningLifecycleResult['updateProgramAudience'] = useCallback(
    async (programId, rules) => {
      const { idtoken, authorization } = getAuthHeaders();
      const normalized = participatingAudienceRules(rules);
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === programId
            ? {
                ...p,
                audienceRules: normalized.length > 0 ? normalized : null,
                // Keep the legacy mirror consistent so the label row stays
                // correct until the next refetch.
                audience: normalized.length > 0 ? flattenAudienceRules(normalized) : null
              }
            : p
        )
      );
      try {
        await API.patchProgram({
          id: programId,
          body: { audienceRules: normalized },
          idtoken,
          authorization,
          location
        });
        // Removals change membership counts the card doesn't own — refetch so
        // enrolled/progress figures reflect the sync the BE just performed.
        await fetchAll();
        return true;
      } catch (e) {
        await fetchAll();
        return false;
      }
    },
    [location, fetchAll]
  );

  const updateProgramOwner: UseAdminLearningLifecycleResult['updateProgramOwner'] = useCallback(
    async (programId, ownerId) => {
      const { idtoken, authorization } = getAuthHeaders();
      const owner = ownerId ? orgMembers.find((m) => m.id === ownerId) ?? null : null;
      setPrograms((prev) => prev.map((p) => (p.id === programId ? { ...p, owner } : p)));
      try {
        await API.patchProgram({
          id: programId,
          body: { ownerId },
          idtoken,
          authorization,
          location
        });
        return true;
      } catch (e) {
        await fetchAll();
        return false;
      }
    },
    [location, fetchAll, orgMembers]
  );

  const updateProgramType: UseAdminLearningLifecycleResult['updateProgramType'] = useCallback(
    async (programId, programType) => {
      const { idtoken, authorization } = getAuthHeaders();
      // Optimistic — flip the category dot immediately. Revert via
      // refetch if the BE rejects (e.g. 403 for a non-admin).
      setPrograms((prev) => prev.map((p) => (p.id === programId ? { ...p, programType } : p)));
      try {
        await API.patchProgram({
          id: programId,
          body: { programType },
          idtoken,
          authorization,
          location
        });
        return true;
      } catch (e: any) {
        // A refusal the admin can act on (a program whose structure is built can't move into or
        // out of Role Excellence) says why; the card is put back either way.
        if (e?.response?.status === 409 && e.response.data?.message) toast.info(e.response.data.message);
        await fetchAll();
        return false;
      }
    },
    [location, fetchAll]
  );

  const createProgramFromDrawer: UseAdminLearningLifecycleResult['createProgramFromDrawer'] = useCallback(
    async (input) => {
      if (!orgMongoId || !orgParentGroupId) {
        // Surfaced instead of failing silently: this is the most common
        // "can't add a program" cause — the orgDetails slice (which carries
        // the Mongo _id + parent grpId that /group/create requires) hasn't
        // populated for this org. Log the exact gap so it's diagnosable.
        console.error('[LL] createProgramFromDrawer blocked — missing org identifiers', {
          orgMongoId,
          orgParentGroupId
        });
        toast.error(
          'The program could not be built: organization details are still loading. Refresh the page and try again.'
        );
        return { id: null };
      }
      const { idtoken: _idtoken, authorization: _authorization } = getAuthHeaders();
      void _idtoken;
      void _authorization;
      try {
        // /group/create expects Mongo identifiers (legacy contract — the
        // handler looks up `parent` via getGroup keyed on _id, and stores
        // `orgId` on the new group's document where v3AdminPrograms later
        // queries it). Match what ManualProgramBuilder sends: org _id +
        // org.grpId.
        const res = await API.createGroup({
          name: input.name.trim(),
          orgId: orgMongoId,
          parent: orgParentGroupId,
          location,
          programType: input.programType,
          learningPillarId: input.learningPillarId,
          launchDate: input.launchDate ?? null,
          ownerId: input.ownerId ?? null,
          // With audience groups they are the audience: the BE derives the roles, the narrowing and the
          // enrolment mode from them, so none of those goes beside them.
          ...(input.audienceGroups
            ? { audienceGroups: input.audienceGroups }
            : {
                // group/create still takes the flat list; the BE buckets it into
                // per-list "any" rules. Sent alongside audienceRules so an older BE
                // build keeps working.
                memberAttributes: flattenAudienceRules(input.audienceRules),
                audienceRules: participatingAudienceRules(input.audienceRules),
                enrollmentMode: input.enrollmentMode ?? null,
                roleIds: input.roleIds
              }),
          awaitingInviteFinish: input.awaitingInviteFinish === true,
          capabilityIds: input.capabilityIds,
          format: input.format,
          guided: input.guided,
          ...(input.goalLessons ? { goalLessons: true } : {})
        });
        const createdId: string | null =
          res?.data?.groupDetails?.grpId ||
          res?.data?.data?._id ||
          res?.data?._id ||
          res?.data?.data?.id ||
          res?.data?.id ||
          null;
        // Kick off a background refetch so the new card materialises with
        // BE-computed fields (status, programCompletionRate, etc.).
        // Not awaited — callers get the id immediately so Design Now can
        // navigate and Design Later can close the drawer without blocking
        // on the 3 downstream GETs completing.
        fetchAll();
        return { id: createdId };
      } catch (e: any) {
        // Surface the real BE failure instead of swallowing it — this catch
        // was hiding /group/create errors (auth, validation, 5xx), making
        // "add program" look like a no-op on the customer demo.
        console.error('[LL] createGroup failed', e?.response?.status, e?.response?.data || e);
        // The audience groups refused for someone who would be in both: the caller shows it beside the groups.
        const refusal = input.audienceGroups ? audienceGroupsRefusalOf(e) : null;
        if (refusal?.overlaps) return { id: null, refusal };
        toast.error(
          e?.response?.data?.message || e?.message || 'The program could not be built. Please try again.'
        );
        return { id: null };
      }
    },
    [orgMongoId, orgParentGroupId, location, fetchAll]
  );

  const publishMentorshipProgram: UseAdminLearningLifecycleResult['publishMentorshipProgram'] =
    useCallback(
      async (programId) => {
        const { idtoken, authorization, orgid } = getAuthHeaders();
        try {
          await API.patchProgram({
            id: programId,
            body: { goalPublished: true, settingsUpdate: true },
            idtoken,
            authorization,
            location,
            orgid
          });
          return true;
        } catch (e: any) {
          console.error('[LL] publishMentorshipProgram failed', e?.response?.status, e?.response?.data || e);
          return false;
        }
      },
      [location]
    );

  const inviteAdmin: UseAdminLearningLifecycleResult['inviteAdmin'] = useCallback(
    async ({ firstName, lastName, email }) => {
      // orgId from useProgramContext is the Auth0 org identifier the
      // org/{authId}/members/invite route expects.
      if (!orgId) return false;
      try {
        await API.inviteOrgMember(orgId, {
          firstName,
          lastName,
          email,
          role: ['admin']
        });
        return true;
      } catch (e: any) {
        console.error('[LL] inviteAdmin failed', e?.response?.status, e?.response?.data || e);
        return false;
      }
    },
    [orgId]
  );

  // Invite Org Admin → existing member: grant them the org admin role.
  const promoteOrgAdmin: UseAdminLearningLifecycleResult['promoteOrgAdmin'] = useCallback(
    async (userId) => {
      if (!orgId || !userId) return false;
      try {
        await API.promoteOrgAdmin(orgId, userId);
        return true;
      } catch (e: any) {
        console.error('[LL] promoteOrgAdmin failed', e?.response?.status, e?.response?.data || e);
        return false;
      }
    },
    [orgId]
  );

  return {
    pillars,
    programs,
    orgMembers,
    loading,
    error,
    refetch: fetchAll,
    createPillar,
    updatePillar,
    deletePillar,
    moveProgram,
    reorderPrograms,
    updateProgramLaunchDate,
    updateProgramAudience,
    previewProgramAudience,
    updateProgramOwner,
    updateProgramType,
    createProgramFromDrawer,
    publishMentorshipProgram,
    inviteAdmin,
    promoteOrgAdmin
  };
};
